import { useMemo, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useBookmarkSelection } from '../../contexts/BookmarkSelectionContext';
import { useBatchActions } from '../../hooks/useBatchActions';
import { useCurrentUser } from '../../services/userHooks';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import { tagExists } from '../../services/tagService';
import { CreateTagItem, TagSelectionItem } from '../Tag/TagComponents';
import Icon from '../ui/Icon';
import { BatchTagPopoverProps } from './types';

function BatchTagPopoverContent({
  batchTarget,
  closePopover,
}: BatchTagPopoverProps & { closePopover: () => void }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const { batchTag, batchSectionTag } = useBatchActions();
  const { currentUser } = useCurrentUser();
  const { disableBatchMode } = useBookmarkSelection();

  const isPending =
    batchTarget.kind === 'section' ? batchSectionTag.isPending : batchTag.isPending;

  const availableTags = useMemo(() => currentUser?.tags || [], [currentUser?.tags]);
  const filteredTags = availableTags.filter((tag) =>
    tag.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const stagedNewTagNames = useMemo(() => {
    const existingNames = new Set(availableTags.map((tag) => tag.name.toLowerCase()));
    return selectedTags.filter((name) => !existingNames.has(name.toLowerCase()));
  }, [selectedTags, availableTags]);

  const visibleStagedNewTagNames = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return stagedNewTagNames.filter((name) => name.toLowerCase().includes(query));
  }, [stagedNewTagNames, searchQuery]);

  const shouldShowCreateTag = useMemo(() => {
    const trimmed = searchQuery.trim();
    return (
      trimmed.length > 0 &&
      !tagExists(trimmed, availableTags) &&
      !selectedTags.some((name) => name.toLowerCase() === trimmed.toLowerCase())
    );
  }, [searchQuery, availableTags, selectedTags]);

  const { selectedIndex, handleItemHover, handleItemClick } = useKeyboardNavigation({
    items: filteredTags,
    onSelect: (tag) => handleTagToggle(tag.name),
    onEscape: closePopover,
    isActive: true,
  });

  const handleTagToggle = (tagName: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagName) ? prev.filter((t) => t !== tagName) : [...prev, tagName],
    );
  };

  const handleCreateTag = () => {
    const tagName = searchQuery.trim();
    if (!tagName) return;
    setSelectedTags((prev) => (prev.includes(tagName) ? prev : [...prev, tagName]));
    setSearchQuery('');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && shouldShowCreateTag) {
      e.preventDefault();
      handleCreateTag();
    }
  };

  const handleApply = async () => {
    if (selectedTags.length === 0) return;

    if (batchTarget.kind === 'section') {
      await batchSectionTag.mutateAsync({
        section: batchTarget.section,
        addTags: selectedTags,
      });
    } else {
      await batchTag.mutateAsync({
        bookmarkIds: batchTarget.bookmarkIds,
        addTags: selectedTags,
      });
    }

    disableBatchMode();
    closePopover();
  };

  return (
    <div className="tag-actions-container" onClick={(e) => e.stopPropagation()}>
      <div className="tag-input-container flex items-center">
        <Icon name="search" />
        <input
          type="text"
          className="tag-actions-input flex-1"
          placeholder={t`Add tag`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          autoFocus
        />
      </div>

      <div className="available-tags-section">
        {shouldShowCreateTag && (
          <CreateTagItem
            tagName={searchQuery.trim()}
            onClick={handleCreateTag}
            disabled={isPending}
          />
        )}
        <div className="available-tags-container">
          {filteredTags.length === 0 &&
          visibleStagedNewTagNames.length === 0 &&
          !shouldShowCreateTag ? (
            <div className="px-4 py-8 text-center text-sm text-gray-500">
              {searchQuery ? (
                <Trans>No matching tags found</Trans>
              ) : (
                <Trans>No available tags</Trans>
              )}
            </div>
          ) : (
            <>
              {visibleStagedNewTagNames.map((name) => (
                <TagSelectionItem
                  key={`new:${name}`}
                  tag={{ id: `new:${name}`, name, slug: name, count: 0 }}
                  isSelected={true}
                  onToggle={handleTagToggle}
                  onClick={() => handleTagToggle(name)}
                  disabled={isPending}
                  showCount={false}
                />
              ))}
              {filteredTags.map((tag, index) => (
                <TagSelectionItem
                  key={tag.id}
                  tag={tag}
                  isSelected={selectedTags.includes(tag.name)}
                  isHighlighted={selectedIndex === index}
                  onToggle={handleTagToggle}
                  onMouseEnter={() => handleItemHover(index)}
                  onClick={() => handleItemClick(index)}
                  disabled={isPending}
                  showCount={true}
                />
              ))}
            </>
          )}
        </div>
      </div>

      <div className="mt-3 flex justify-end space-x-2 px-4 pb-4">
        <button onClick={closePopover} className="button-ghost">
          <Trans>Cancel</Trans>
        </button>
        <button
          onClick={handleApply}
          disabled={selectedTags.length === 0 || isPending}
          className="button-primary disabled:opacity-50"
        >
          {isPending ? <Trans>Adding...</Trans> : <Trans>Add</Trans>}
        </button>
      </div>
    </div>
  );
}

function BatchTagPopover({ batchTarget, onClose }: BatchTagPopoverProps) {
  return (
    <BatchTagPopoverContent
      batchTarget={batchTarget}
      onClose={onClose}
      closePopover={onClose}
    />
  );
}

export default BatchTagPopover;
